import { Col, Row } from "antd";
import { useRouteMatch } from "react-router";
import { Box } from "@chakra-ui/layout";

import { useBreadcrumb } from "features/breadcrumbs";

import { showSuccess } from "infra/helpers/alerta";
import { useMemo, useState } from "react";
import MenuDetalhes from "components/Menu/MenuDetalhes";
import { TabMenuOptions, TabsMenuOptionsRoutes } from "./typings";
import history from "infra/helpers/history";
import FormCadastroUnidadeGeral from "components/Features/Unidades/components/FormCadastroUnidadeGeral";
import CardDefault from "components/Cards/CardDefault";
import TabPessoas from "./components/TabPessoas";
import useTenantTipoEstruturaMoradia from "features/tenants/hooks/useTenantTipoEstruturaMoradia";
import { useLocation } from "react-router-dom";
import TabPets from "./components/TabPets";
import TabVeiculos from "./components/TabVeiculos";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import Dashboard from "./components/Dashboard";
import { useLoading } from "components/GlobalLoading";
import { useInvalidateQuery } from "infra/packages/react-query";
import PageHeader from "components/Page/PageHeader";
import useUnidade from "features/unidades/useUnidade";
import ParametrizacaoDeCobranca from "./components/ParametrizacaoDeCobranca/index";
import DocumentosUnidade from "./components/DocumentosUnidade/index";
import Container from "components/Container";
import HistoricoCobranca from "./components/HistoricoCobranca/index";
import { parse } from "query-string";

function UnidadeDetalhesPage() {
  const { doInvalidateQueryWithStringKey } = useInvalidateQuery();

  const route = useRouteMatch<{ id: string; tenant: string; page?: string }>();
  const { data: unidade, refetch: refetchUnidade } = useUnidade(
    route.params.id
  );

  const page = parse(window.location.search)?.page;

  const breadcrumb = useMemo(() => {
    if (route.params.tenant) {
      return [
        {
          name: "Condomínios",
          url: `/condominio`,
        },
        {
          name: "Unidades",
          url: `/condominio/${route.params.tenant}?opcao=unidades${
            page ? `&page=${page}` : ""
          }`,
        },
        { name: route.params.id ? `Editar unidade` : "Nova unidade" },
      ];
    }

    return [
      { name: "Administração" },
      { name: "Cadastros" },
      {
        name: "Unidades",
        url: `/cadastros/unidades${page ? `?page=${page}` : ""}`,
      },
      {
        name: route.params.id ? `Editar unidade` : "Nova unidade",
      },
    ];
  }, [route.params.id, route.params.tenant, page]);

  useBreadcrumb(breadcrumb);

  const { search } = useLocation();
  const {
    tenant: { id: tenantId },
  } = useCurrentTenant();

  const params = new URLSearchParams(search);
  const opcaoParam =
    (params.get("opcao") as string) || TabsMenuOptionsRoutes.Dashboard;

  const { data: tenant, isLoading } = useTenantTipoEstruturaMoradia(
    route.params.tenant! || tenantId
  );

  useLoading(isLoading);

  const [selectedMenuOption, setSelectedMenuOption] = useState(opcaoParam);

  const handleChangeMenu = (tab: string) => {
    setSelectedMenuOption(tab);

    if (route.params.id) {
      if (route.params.tenant) {
        history.push(
          `/condominio/${route.params.tenant}/unidades/${route.params.id}?opcao=${tab}`
        );
      } else {
        history.push(`/cadastros/unidades/${route.params.id}?opcao=${tab}`);
      }
    } else {
      if (route.params.tenant) {
        history.push(
          `/condominio/${route.params.tenant}/unidades/new?opcao=${tab}`
        );
      } else {
        history.push(`/cadastros/unidades/new?opcao=${tab}`);
      }
    }
  };

  if (!opcaoParam) {
    return null;
  }

  return (
    <Container pb="32px">
      {unidade ? <PageHeader title={`${unidade.numero}`} /> : null}

      <Row gutter={32}>
        <Col sm={6}>
          <Box mb={4} pt="0px" flex={1}>
            <MenuDetalhes<TabsMenuOptionsRoutes>
              disabledOptions={
                !route.params.id
                  ? [
                      TabsMenuOptionsRoutes.Dashboard,
                      TabsMenuOptionsRoutes.Pessoas,
                      TabsMenuOptionsRoutes.Pets,
                      TabsMenuOptionsRoutes.Veiculos,
                      TabsMenuOptionsRoutes.Documentos,
                      TabsMenuOptionsRoutes.ParametrizacaoDeCobranca,
                      TabsMenuOptionsRoutes.HistoricoCobranca,
                    ]
                  : []
              }
              options={TabMenuOptions}
              selectedOption={selectedMenuOption as TabsMenuOptionsRoutes}
              onChangeOption={(tab) => {
                handleChangeMenu(tab);
              }}
            />
          </Box>
        </Col>
        {tenant ? (
          <Col sm={18}>
            {selectedMenuOption === TabsMenuOptionsRoutes.Dados ? (
              <CardDefault>
                <FormCadastroUnidadeGeral
                  tenantId={route.params.tenant! || tenantId}
                  tipoEstruturaMoradia={tenant.tipoEstruturaMoradia!}
                  onSaved={(id) => {
                    if (route.params.id) {
                      refetchUnidade();
                    }

                    showSuccess().then(() => {
                      if (!route.params.id) {
                        if (route.params.tenant) {
                          history.push(
                            `/condominio/${route.params.tenant}/unidades/${id}?opcao=${TabsMenuOptionsRoutes.Pessoas}`
                          );
                        } else {
                          history.push(
                            `/cadastros/unidades/${id}?opcao=${TabsMenuOptionsRoutes.Pessoas}`
                          );
                        }

                        doInvalidateQueryWithStringKey(
                          "v1/tenants/passos-cadastro"
                        );
                        window.location.reload();
                      }
                    });
                  }}
                  onClose={() => history.goBack()}
                  id={route.params.id ?? null}
                />
              </CardDefault>
            ) : null}

            {selectedMenuOption === TabsMenuOptionsRoutes.Dashboard ? (
              <Dashboard unidadeId={route.params.id} />
            ) : null}

            {selectedMenuOption === TabsMenuOptionsRoutes.Pessoas ? (
              <TabPessoas
                tenantId={route.params.tenant || tenantId}
                unidadeId={route.params.id}
              />
            ) : null}

            {selectedMenuOption === TabsMenuOptionsRoutes.Veiculos ? (
              <TabVeiculos unidadeId={route.params.id} />
            ) : null}

            {selectedMenuOption === TabsMenuOptionsRoutes.Pets ? (
              <TabPets unidadeId={route.params.id} />
            ) : null}
            {selectedMenuOption === TabsMenuOptionsRoutes.Documentos ? (
              <DocumentosUnidade unidadeId={route.params.id} />
            ) : null}
            {selectedMenuOption ===
            TabsMenuOptionsRoutes.ParametrizacaoDeCobranca ? (
              <ParametrizacaoDeCobranca unidadeId={route.params.id} />
            ) : null}
            {selectedMenuOption === TabsMenuOptionsRoutes.HistoricoCobranca ? (
              <HistoricoCobranca
                unidadeId={route.params.id}
                tenantId={route.params.tenant || tenantId}
              />
            ) : null}
          </Col>
        ) : null}
      </Row>
    </Container>
  );
}

export default UnidadeDetalhesPage;
